import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { type DragEvent, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Waypoint } from 'react-waypoint';
import { useSingleEmailComposer, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { formatFileSize } from '../../../../../../utils/fileSize';
import { createRawRichTextContentStateWithHtmlStringValue } from '../../../../../../extViewmodels/sdk/Utils';
import { EmailMessageViewModel } from '../../../../../../viewmodels/AppViewModels';
import { FileRequestLinkPlaceholder, FileSendLinkPlaceholder } from '../../../../../../models/Token';
import { isFilesFeatureEnabled } from '../../../../../../models/utils/accountUtils';
import { useContact, useContactFilesQuery, useDeleteSharedFileMutation } from '../../../../../../queries';
import { TrashIcon } from '../../../../../components/svgs/icons/TrashIcon';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../../../components/DataGrid';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { Placeholder } from '../../../../../components/Placeholder';
import { FileTextIcon } from '../../../../../components/svgs/icons/FileTextIcon';
import { Button } from '../../../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../../components-v2/Dialog';
import { Checkbox } from '../../../../../components-v2/Checkbox';
import { Pill } from '../../../../../components-v2/Pill';
import { styleSheet } from './styles';

const MAX_FILE_SIZE_BYTES = 20 * 1_000_000;

function filterByMaxSize(files: File[]): { accepted: File[]; rejected: File[] } {
	const accepted: File[] = [];
	const rejected: File[] = [];
	for (const file of files) {
		if (file.size <= MAX_FILE_SIZE_BYTES) {
			accepted.push(file);
		} else {
			rejected.push(file);
		}
	}
	return { accepted, rejected };
}

function filterByTotalSize(newFiles: File[], currentBytes: number): { accepted: File[]; rejected: File[] } {
	const accepted: File[] = [];
	const rejected: File[] = [];
	let running = currentBytes;
	for (const file of newFiles) {
		if (running + file.size <= MAX_FILE_SIZE_BYTES) {
			accepted.push(file);
			running += file.size;
		} else {
			rejected.push(file);
		}
	}
	return { accepted, rejected };
}

const SOURCE_TYPE_LABELS: Record<Api.SharedFileSourceType, string> = {
	[Api.SharedFileSourceType.Unknown]: 'Unknown',
	[Api.SharedFileSourceType.Note]: 'Note',
	[Api.SharedFileSourceType.Survey]: 'Survey',
	[Api.SharedFileSourceType.Automation]: 'Automation',
	[Api.SharedFileSourceType.SingleEmail]: 'Single Email',
	[Api.SharedFileSourceType.BulkEmailMessage]: 'Bulk Email',
	[Api.SharedFileSourceType.HtmlNewsletter]: 'Newsletter',
	[Api.SharedFileSourceType.SecureSend]: 'Secure Send',
	[Api.SharedFileSourceType.SecureRequest]: 'Secure Request',
	[Api.SharedFileSourceType.TextMessage]: 'Text Message',
};

function humanizeSourceType(sourceType: Api.SharedFileSourceType): string {
	return SOURCE_TYPE_LABELS[sourceType] ?? sourceType;
}

function formatDate(dateStr?: string): string {
	if (!dateStr) {
		return '';
	}
	return new Date(dateStr).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function getUploaderName(
	direction: Api.SharedFileDirection,
	creator?: Api.UserReference,
	contact?: Api.IContact
): string {
	if (direction === Api.SharedFileDirection.Received) {
		return [contact?.firstName, contact?.lastName].filter(Boolean).join(' ');
	}
	if (!creator) {
		return '';
	}
	return creator.fullName ?? [creator.firstName, creator.lastName].filter(Boolean).join(' ');
}

type IProps = {
	contactId?: string;
};

export const ContactFiles = ({ contactId }: IProps) => {
	const userSession = useUserSession();
	const singleEmailComposer = useSingleEmailComposer();
	const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
	const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
	const [modalStep, setModalStep] = useState<null | 'upload' | 'confirm'>(null);
	const [filesToConfirm, setFilesToConfirm] = useState<{ name: string }[]>([]);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
	const [filesToSend, setFilesToSend] = useState<File[]>([]);
	const [dragActive, setDragActive] = useState(false);
	const [uploadMode, setUploadMode] = useState<'upload' | 'send'>('upload');
	const [fileToDelete, setFileToDelete] = useState<Api.ISharedFile | null>(null);

	const handleRequestFiles = () => {
		const contactVm = new Api.ContactViewModel(userSession, { id: contactId } as Api.IContact);
		const emailMessage = new EmailMessageViewModel<File>(userSession, true);
		emailMessage.contactsToAdd.add(contactVm);
		emailMessage.options.sendSingleEmail = true;
		emailMessage.content = createRawRichTextContentStateWithHtmlStringValue(
			`<p>${FileRequestLinkPlaceholder.symbol}</p>`
		);
		singleEmailComposer.showWithEmailMessage(emailMessage, [contactVm]);
	};

	const contactQuery = useContact({ contactId: contactId ?? '', enabled: !!contactId });

	const filesQuery = useContactFilesQuery({
		contactId: contactId ?? '',
		enabled: !!contactId,
	});

	const { fetchNextPage, hasNextPage, isFetchingNextPage } = filesQuery;

	const deleteSharedFileMutation = useDeleteSharedFileMutation({
		onSuccess: () => {
			toast.success('File deleted');
			setFileToDelete(null);
		},
		onError: () => {
			toast.error('Failed to delete file');
		},
	});

	const files = useMemo(
		() => filesQuery.data?.pages.flatMap((page: Api.IPagedCollection<Api.ISharedFile>) => page.values) ?? [],
		[filesQuery.data]
	);

	const handleDownload = async (file: Api.ISharedFile) => {
		setDownloadingFileId(file.id);
		try {
			const attachment = await userSession.webServiceHelper.callAsync<Api.IFileAttachment>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `attachment/${file.fileAttachment.id}` }),
				'GET'
			);
			if (attachment.url) {
				const link = document.createElement('a');
				link.href = attachment.url;
				link.download = attachment.fileName;
				link.click();
				link.remove();
			}
		} catch {
			toast.error('Failed to download file');
		} finally {
			setDownloadingFileId(null);
		}
	};

	const handleSendFilesClick = () => {
		if (selectedFileIds.size > 0) {
			const selected = files
				.filter((f: Api.ISharedFile) => selectedFileIds.has(f.id))
				.map((f: Api.ISharedFile) => ({ name: f.fileAttachment.fileName }));
			setFilesToConfirm(selected);
			setModalStep('confirm');
		} else {
			setUploadMode('send');
			setModalStep('upload');
		}
	};

	const handleModalClose = () => {
		setModalStep(null);
		setUploadedFiles([]);
		setFilesToSend([]);
		setDragActive(false);
	};

	const handleUploadNext = () => {
		if (uploadMode === 'send') {
			setFilesToConfirm(uploadedFiles.map(f => ({ name: f.name })));
			setFilesToSend(uploadedFiles);
			setUploadedFiles([]);
			setModalStep('confirm');
		} else {
			toast.success('Files uploaded!');
			setModalStep(null);
			setUploadedFiles([]);
		}
	};

	const handleDrag = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		const dropped = Array.from(e.dataTransfer.files ?? []);
		const { accepted: sizeOk, rejected: tooLarge } = filterByMaxSize(dropped);
		if (tooLarge.length > 0) {
			toast.error(
				`${tooLarge.length} file${tooLarge.length !== 1 ? 's' : ''} removed — files must be no larger than 20 MB`
			);
		}
		const currentBytes = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
		const { accepted, rejected: overTotal } = filterByTotalSize(sizeOk, currentBytes);
		if (overTotal.length > 0) {
			toast.error(
				`${overTotal.length} file${overTotal.length !== 1 ? 's' : ''} removed — total upload size cannot exceed 20 MB`
			);
		}
		if (accepted.length > 0) {
			setUploadedFiles(prev => [...prev, ...accepted]);
		}
	};

	const handleConfirmSend = () => {
		const contactVm = new Api.ContactViewModel(userSession, { id: contactId } as Api.IContact);
		const emailMessage = new EmailMessageViewModel<File>(userSession, true);
		emailMessage.contactsToAdd.add(contactVm);
		emailMessage.options.sendSingleEmail = true;
		emailMessage.sendAttachmentsAsLink = true;
		emailMessage.content = createRawRichTextContentStateWithHtmlStringValue(`<p>${FileSendLinkPlaceholder.symbol}</p>`);

		if (filesToSend.length > 0) {
			const maxBytes = EmailMessageViewModel.MaxByteCount;
			const attachmentsVm = new Api.AttachmentsToBeUploadedViewModel<File>(filesToSend, maxBytes);
			emailMessage.setAttachments(attachmentsVm);
		} else {
			const selectedFiles = files
				.filter((f: Api.ISharedFile) => selectedFileIds.has(f.id))
				.map((f: Api.ISharedFile) => ({
					fileName: f.fileAttachment.fileName,
					fileSize: f.fileAttachment.fileSize,
					id: f.fileAttachment.id,
				}));
			emailMessage.setSavedAttachments(selectedFiles);
		}

		singleEmailComposer.showWithEmailMessage(emailMessage, [contactVm]);
		setModalStep(null);
		setSelectedFileIds(new Set());
		setFilesToConfirm([]);
		setFilesToSend([]);
	};

	const filesGroupPermissions = userSession.user?.userGroupPermissions?.featurePermissions?.files;
	const canSendFiles =
		isFilesFeatureEnabled(userSession.account) &&
		!!userSession.account?.features?.files?.canSendFileLinks &&
		!!filesGroupPermissions?.sendDownloadLink;
	const canRequestFiles = isFilesFeatureEnabled(userSession.account) && !!filesGroupPermissions?.requestFile;
	const canDeleteFiles = isFilesFeatureEnabled(userSession.account) && !!filesGroupPermissions?.delete;

	const allSelected = files.length > 0 && files.every((f: Api.ISharedFile) => selectedFileIds.has(f.id));
	const selectAllState = allSelected ? true : selectedFileIds.size > 0 ? 'indeterminate' : false;

	if (!contactId) {
		return;
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.actionsContainer)}>
					{canSendFiles && (
						<Button variant='secondary' onClick={handleSendFilesClick}>
							{selectedFileIds.size > 0
								? `Send ${selectedFileIds.size} File${selectedFileIds.size !== 1 ? 's' : ''}`
								: 'Send Files'}
						</Button>
					)}
					{canRequestFiles && (
						<Button variant='secondary' onClick={handleRequestFiles}>
							Request Files
						</Button>
					)}
				</div>
			</div>

			<div className={css(styleSheet.content)}>
				<Placeholder
					isLoading={filesQuery.isPending}
					isEmpty={!filesQuery.isPending && files.length === 0}
					message='No files have been shared with this contact yet.'
				/>
				{!filesQuery.isPending && files.length > 0 && (
					<div className={css(styleSheet.dataGridWrapper)}>
						<DataGrid styles={[styleSheet.dataGrid]}>
							<DataGridColumns styles={[styleSheet.dataGridHeader]}>
								{canSendFiles && <DataGridColumn styles={[styleSheet.checkboxColumn]} />}
								<DataGridColumn>Filename</DataGridColumn>
								<DataGridColumn>Date</DataGridColumn>
								<DataGridColumn>Source</DataGridColumn>
								<DataGridColumn>Uploader</DataGridColumn>
								<DataGridColumn>Size</DataGridColumn>
								{canDeleteFiles && <DataGridColumn styles={[styleSheet.deleteColumn]} />}
							</DataGridColumns>
							<DataGridRows>
								{canSendFiles && (
									<DataGridRow styles={[styleSheet.selectAllRow]}>
										<DataGridRowItem noTopBorder fullColSpan styles={[styleSheet.selectAllCell]}>
											<Checkbox
												aria-label='Select all files'
												checked={selectAllState}
												onCheckedChange={checked => {
													setSelectedFileIds(
														checked === true ? new Set(files.map((f: Api.ISharedFile) => f.id)) : new Set()
													);
												}}
											/>
											<span className={css(styleSheet.selectAllLabel)}>Select All</span>
										</DataGridRowItem>
									</DataGridRow>
								)}
								{files.map((file: Api.ISharedFile) => {
									const isDownloading = downloadingFileId === file.id;
									return (
										<DataGridRow key={file.id} styles={[styleSheet.dataGridRow]}>
											{canSendFiles && (
												<DataGridRowItem
													styles={[styleSheet.checkboxColumn, styleSheet.dataGridCell, styleSheet.cellCheckbox]}
												>
													<Checkbox
														aria-label={`Select ${file.fileAttachment.fileName}`}
														checked={selectedFileIds.has(file.id)}
														onCheckedChange={checked => {
															setSelectedFileIds(prev => {
																const next = new Set(prev);
																if (checked === true) next.add(file.id);
																else next.delete(file.id);
																return next;
															});
														}}
													/>
												</DataGridRowItem>
											)}
											<DataGridRowItem styles={[styleSheet.dataGridCell, styleSheet.cellFilename]}>
												<div className={css(styleSheet.filenameCell)}>
													<FileTextIcon width={20} height={20} className={css(styleSheet.fileIcon)} />
													<div className={css(styleSheet.fileDetails)}>
														<Button
															variant='link'
															disabled={isDownloading}
															onClick={() => handleDownload(file)}
															title='Download'
															styles={[styleSheet.filenameLink]}
														>
															{file.fileAttachment.fileName}
														</Button>
														<div className={css(styleSheet.fileMeta)}>
															<span className={css(styleSheet.fileMetaLine)}>
																{formatDate(file.creationDate)}
																<span className={css(styleSheet.sourceBadge)}>
																	{humanizeSourceType(file.source.sourceType)}
																</span>
															</span>
															<span className={css(styleSheet.fileMetaLine)}>
																{[
																	getUploaderName(file.direction, file.creator, contactQuery.data),
																	formatFileSize(file.fileAttachment.fileSize),
																]
																	.filter(Boolean)
																	.join(' · ')}
															</span>
														</div>
													</div>
												</div>
											</DataGridRowItem>
											<DataGridRowItem styles={[styleSheet.dataGridCell, styleSheet.cellHiddenWhenStacked]}>
												{formatDate(file.creationDate)}
											</DataGridRowItem>
											<DataGridRowItem styles={[styleSheet.dataGridCell, styleSheet.cellHiddenWhenStacked]}>
												<Pill
													variant='label'
													color='gray'
													label={humanizeSourceType(file.source.sourceType)}
													size='small'
												/>
											</DataGridRowItem>
											<DataGridRowItem styles={[styleSheet.dataGridCell, styleSheet.cellHiddenWhenStacked]}>
												{getUploaderName(file.direction, file.creator, contactQuery.data)}
											</DataGridRowItem>
											<DataGridRowItem styles={[styleSheet.dataGridCell, styleSheet.cellHiddenWhenStacked]}>
												{formatFileSize(file.fileAttachment.fileSize)}
											</DataGridRowItem>
											{canDeleteFiles && (
												<DataGridRowItem
													styles={[styleSheet.deleteColumn, styleSheet.dataGridCell, styleSheet.cellDelete]}
												>
													{file.direction === Api.SharedFileDirection.Received && (
														<Button
															variant='link'
															onClick={() => setFileToDelete(file)}
															aria-label={`Delete ${file.fileAttachment.fileName}`}
														>
															<TrashIcon />
														</Button>
													)}
												</DataGridRowItem>
											)}
										</DataGridRow>
									);
								})}
							</DataGridRows>
						</DataGrid>
					</div>
				)}
				{hasNextPage && !isFetchingNextPage && <Waypoint bottomOffset='-200px' onEnter={() => fetchNextPage()} />}
				{isFetchingNextPage && <LoadingSpinner type='extra-tiny' />}
			</div>
			<Dialog
				open={modalStep === 'confirm'}
				onOpenChange={open => {
					if (!open) setModalStep(null);
				}}
			>
				<DialogPopup showCloseButton={false}>
					<DialogHeader>
						<DialogTitle>Are you sure you want to send these files?</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.fileRows)}>
							{filesToConfirm.map((f, i) => (
								<div key={`${f.name}-${i}`} className={css(styleSheet.fileRow)}>
									<FileTextIcon width={16} height={16} className={css(styleSheet.fileIcon)} />
									<span className={css(styleSheet.fileRowName)}>{f.name}</span>
								</div>
							))}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button variant='secondary' onClick={() => setModalStep(null)}>
							Cancel
						</Button>
						<Button onClick={handleConfirmSend}>Confirm</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
			<Dialog
				open={modalStep === 'upload'}
				onOpenChange={open => {
					if (!open) handleModalClose();
				}}
			>
				<DialogPopup showCloseButton={false}>
					<DialogHeader>
						<DialogTitle>Send Files</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div
							className={css(styleSheet.dropzone, dragActive && styleSheet.dropzoneActive)}
							onDragEnter={handleDrag}
							onDragLeave={handleDrag}
							onDragOver={handleDrag}
							onDrop={handleDrop}
							onClick={() => fileInputRef.current?.click()}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									fileInputRef.current?.click();
								}
							}}
							role='button'
							tabIndex={0}
							aria-label='Drop files here or click to browse'
						>
							<div className={css(styleSheet.dropzoneContent)}>
								<p className={css(styleSheet.dropzoneTitle)}>Drop files here or click to browse</p>
								<p className={css(styleSheet.dropzoneDescription)}>Select one or more files to send</p>
							</div>
							<input
								ref={fileInputRef}
								type='file'
								multiple
								className={css(styleSheet.hiddenInput)}
								onChange={e => {
									const picked = Array.from(e.target.files ?? []);
									const { accepted: sizeOk, rejected: tooLarge } = filterByMaxSize(picked);
									if (tooLarge.length > 0) {
										toast.error(
											`${tooLarge.length} file${tooLarge.length !== 1 ? 's' : ''} removed — files must be no larger than 20 MB`
										);
									}
									const currentBytes = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
									const { accepted, rejected: overTotal } = filterByTotalSize(sizeOk, currentBytes);
									if (overTotal.length > 0) {
										toast.error(
											`${overTotal.length} file${overTotal.length !== 1 ? 's' : ''} removed — total upload size cannot exceed 20 MB`
										);
									}
									if (accepted.length > 0) setUploadedFiles(prev => [...prev, ...accepted]);
									if (fileInputRef.current) fileInputRef.current.value = '';
								}}
							/>
						</div>
						{uploadedFiles.length > 0 && (
							<div className={css(styleSheet.uploadedFilesList)}>
								<span className={css(styleSheet.filesCount)}>
									{uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} selected
								</span>
								<div className={css(styleSheet.fileRows)}>
									{uploadedFiles.map((f, i) => (
										<div key={`${f.name}-${i}`} className={css(styleSheet.fileRow)}>
											<FileTextIcon width={16} height={16} className={css(styleSheet.fileIcon)} />
											<span className={css(styleSheet.fileRowName)}>{f.name}</span>
											<Button
												variant='link'
												onClick={() => setUploadedFiles(prev => prev.filter((_, idx) => idx !== i))}
												aria-label={`Remove ${f.name}`}
											>
												×
											</Button>
										</div>
									))}
								</div>
							</div>
						)}
					</DialogBody>
					<DialogFooter>
						<Button variant='secondary' onClick={handleModalClose}>
							Cancel
						</Button>
						<Button disabled={uploadedFiles.length === 0} onClick={handleUploadNext}>
							Next
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
			<Dialog
				open={fileToDelete !== null}
				onOpenChange={open => {
					if (!open && !deleteSharedFileMutation.isPending) {
						setFileToDelete(null);
					}
				}}
			>
				<DialogPopup showCloseButton={false}>
					<DialogHeader>
						<DialogTitle>Delete file?</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<p className={css(styleSheet.deleteFileName)}>{fileToDelete?.fileAttachment.fileName}</p>
					</DialogBody>
					<DialogFooter>
						<Button variant='secondary' onClick={() => setFileToDelete(null)}>
							Cancel
						</Button>
						<Button
							disabled={deleteSharedFileMutation.isPending}
							onClick={() => {
								if (!fileToDelete) return;
								deleteSharedFileMutation.mutate({
									id: fileToDelete.id,
									deleteSourceFile: true,
									contactId: contactId ?? '',
								});
							}}
						>
							{deleteSharedFileMutation.isPending ? 'Deleting…' : 'Delete'}
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</div>
	);
};
